import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { brandingInputSchema, DEFAULT_BRAND_NAME, luminance, type BrandingInput } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Button, Card, ErrorText, Field, Input, PageHeader, Spinner } from "../components/ui.tsx";

const MAX_BYTES = 500 * 1024;
const DEFAULTS = brandingInputSchema.parse({});

/** White-label: the name, logo, browser icon and colours everyone sees (sign-in page included). */
export function BrandingPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-branding"], queryFn: () => unwrap(api.admin.branding.$get()) });
  const [f, setF] = useState<BrandingInput>(DEFAULTS);
  const [fileError, setFileError] = useState<string | null>(null);
  useEffect(() => {
    if (q.data) setF(q.data as BrandingInput);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (b: BrandingInput) => unwrap(api.admin.branding.$put({ json: b })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-branding"] });
      void qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });
  const set = (p: Partial<BrandingInput>) => setF((x) => ({ ...x, ...p }));

  const pick = (key: "logo" | "favicon") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileError(null);
    if (file.size > MAX_BYTES) return setFileError(`${file.name} is larger than 500 KB.`);
    if (!/^image\/(png|jpeg|webp|svg\+xml|x-icon|vnd\.microsoft\.icon)$/.test(file.type)) return setFileError("Use a PNG, JPEG, WebP, SVG or ICO image.");
    const r = new FileReader();
    r.onload = () => set({ [key]: String(r.result) });
    r.readAsDataURL(file);
  };

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const sidebarTooLight = /^#[0-9a-fA-F]{6}$/.test(f.sidebarColor) && luminance(f.sidebarColor) > 0.2;
  const dirty = JSON.stringify(f) !== JSON.stringify(q.data);

  return (
    <>
      <PageHeader title="Branding" description="Make this app your own: its name, logo, browser icon and colours, everywhere people see them, including the sign-in page and chat-app messages." />
      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <Card className="space-y-5 p-6">
          <Field label="Product name" hint={`Replaces "${DEFAULT_BRAND_NAME}" in the app, sign-in page, browser tab and Slack/WhatsApp/Discord messages.`}>
            <Input maxLength={40} value={f.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <ImageField label="Logo" hint="Shown on the dark sidebar and sign-in header, so a white or light logo works best. Wide logos up to ~200×40 px look right. Without one, the name is shown." value={f.logo} onPick={pick("logo")} onClear={() => set({ logo: null })} dark={f.sidebarColor} />
          <ImageField label="Browser icon" hint="Square, 64×64 px or larger. Without one, the logo is used." value={f.favicon} onPick={pick("favicon")} onClear={() => set({ favicon: null })} />
          {fileError && <ErrorText error={fileError} />}
          <div className="grid gap-4 sm:grid-cols-2">
            <ColorField label="Accent colour" hint="Links, switches, focus rings." value={f.accentColor} onChange={(v) => set({ accentColor: v })} />
            <ColorField label="Sidebar colour" hint="Sidebar and main buttons. Must be dark: white text sits on it." value={f.sidebarColor} onChange={(v) => set({ sidebarColor: v })} />
          </div>
          {sidebarTooLight && <p className="text-sm text-amber-700">That sidebar colour is too light for white text. Pick a darker one.</p>}
          <ErrorText error={save.error} />
          <div className="flex flex-wrap justify-between gap-2">
            <Button variant="ghost" onClick={() => setF(DEFAULTS)}>Reset to defaults</Button>
            <Button variant="primary" disabled={!dirty || save.isPending || sidebarTooLight} onClick={() => save.mutate(f)}>
              {save.isPending ? <Spinner /> : "Save"}
            </Button>
          </div>
          {save.isSuccess && !dirty && <p className="text-sm text-emerald-700">Saved. Everyone sees it on their next page load.</p>}
        </Card>

        <div>
          <div className="mb-2 text-sm font-medium">Preview</div>
          <Card className="overflow-hidden">
            <div className="space-y-1 p-4" style={{ background: f.sidebarColor }}>
              <div className="mb-4 px-2 pt-1">
                {f.logo ? (
                  <img src={f.logo} alt="" className="h-6 w-auto max-w-full object-contain" />
                ) : f.name === DEFAULT_BRAND_NAME ? (
                  <img src="/mnemio-logo.svg" alt="" className="h-6 w-auto" />
                ) : (
                  <span className="text-lg font-semibold tracking-tight text-white">{f.name}</span>
                )}
              </div>
              <div className="rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white">Chat</div>
              <div className="px-3 py-2 text-sm text-[var(--chrome-text)]">Files</div>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm">
                {f.favicon || f.logo ? <img src={f.favicon ?? f.logo!} alt="" className="h-4 w-4 object-contain" /> : <img src="/favicon.png" alt="" className="h-4 w-4" />}
                <span className="truncate">{f.name}</span>
              </div>
              <button type="button" className="h-9 w-full rounded-lg text-sm font-medium text-white" style={{ background: f.sidebarColor }}>Primary button</button>
              <a className="block text-sm underline" style={{ color: f.accentColor }}>A link</a>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function ImageField({ label, hint, value, onPick, onClear, dark }: { label: string; hint: string; value: string | null; onPick: (e: React.ChangeEvent<HTMLInputElement>) => void; onClear: () => void; dark?: string }) {
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium">{label}</div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="grid h-12 min-w-24 place-items-center rounded-lg border border-[var(--line)] px-3" style={dark ? { background: dark } : undefined}>
          {value ? <img src={value} alt="" className="max-h-8 max-w-48 object-contain" /> : <span className={dark ? "text-xs text-white/60" : "text-xs text-[var(--muted)]"}>default</span>}
        </div>
        <label className="inline-flex h-9 items-center rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3.5 text-sm font-medium hover:bg-[var(--panel-2)]">
          Upload
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,.ico" className="hidden" onChange={onPick} />
        </label>
        {value && <Button variant="ghost" onClick={onClear}>Remove</Button>}
      </div>
      <p className="text-xs text-[var(--muted)]">{hint}</p>
    </div>
  );
}

function ColorField({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex gap-2">
        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-[var(--line)] bg-[var(--panel)] p-1" />
        <Input value={value} onChange={(e) => onChange(e.target.value.trim())} maxLength={7} className="font-mono" />
      </div>
    </Field>
  );
}
